import { useEffect, useState } from 'react';
import { Download, Filter } from 'lucide-react';
import { useToastStore, useUserStore, useAuthStore } from '../store';
import { Spinner, Badge } from '../components/ui';
import { api } from '../utils/api';
import { format } from 'date-fns';

export function ClockRecordsPage() {
  const { addToast } = useToastStore();
  const { users, fetchUsers } = useUserStore();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    targetUserId: '',
  });

  useEffect(() => {
    fetchUsers();
    loadRecords();
  }, []);

  useEffect(() => {
    loadRecords();
  }, [filters]);

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      const result = await api.getClockRecords(
        filters.startDate,
        filters.endDate,
        filters.targetUserId || undefined
      );
      if (result.success && result.data) {
        setRecords(result.data);
      } else {
        addToast(result.error || 'Failed to load clock records', 'error');
      }
    } catch (error) {
      addToast('Failed to load clock records', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    const safeRecords = (records && Array.isArray(records)) ? records : [];
    const csv = [
      ['User', 'Email', 'Shift Date', 'Shift Time', 'Clock In', 'Clock Out', 'Duration (hours)', 'Clock In IP', 'Clock Out IP'].join(','),
      ...safeRecords.map(record => {
        const clockIn = record.clock_in_time ? new Date(record.clock_in_time) : null;
        const clockOut = record.clock_out_time ? new Date(record.clock_out_time) : null;
        const duration = clockIn && clockOut ? ((clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60)).toFixed(2) : '';
        return [
          record.display_name || record.username || '',
          record.email || '',
          record.shift_date || '',
          record.shift_start_time ? `${record.shift_start_time} - ${record.shift_end_time}` : '',
          clockIn ? clockIn.toLocaleString() : '',
          clockOut ? clockOut.toLocaleString() : '',
          duration,
          record.clock_in_ip || '',
          record.clock_out_ip || '',
        ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(',');
      })
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clock-records-${filters.startDate}-to-${filters.endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const safeRecords = (records && Array.isArray(records)) ? records : [];

  if (isLoading && safeRecords.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clock In/Out Records</h1>
          <p className="text-slate-500 mt-1">View and export clock in/out history</p>
        </div>
        <button
          onClick={handleExport}
          className="btn btn-secondary"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
        </div>
        <div className={`grid gap-4 ${isAdmin ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
          <div>
            <label className="input-label">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="input-label">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="input"
            />
          </div>
          {isAdmin && (
            <div>
              <label className="input-label">User</label>
              <select
                value={filters.targetUserId}
                onChange={(e) => setFilters({ ...filters, targetUserId: e.target.value })}
                className="input"
              >
                <option value="">All Users</option>
                {(users && Array.isArray(users) ? users : []).filter(u => u.status === 'Active').map(user => (
                  <option key={user.id} value={user.id}>
                    {user.display_name || user.username}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Records Table */}
      <div className="card overflow-hidden">
        {safeRecords.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No clock records found for the selected filters
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">User</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Shift</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Clock In</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Clock Out</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Duration</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">IP Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {safeRecords.map(record => {
                  const clockIn = record.clock_in_time ? new Date(record.clock_in_time) : null;
                  const clockOut = record.clock_out_time ? new Date(record.clock_out_time) : null;
                  const duration = clockIn && clockOut ? (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60) : null;
                  
                  return (
                    <tr key={record.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">
                            {record.display_name || record.username}
                          </p>
                          <p className="text-sm text-slate-500">{record.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {record.shift_date ? (
                          <div>
                            <p className="text-sm text-slate-900">{format(new Date(record.shift_date + 'T00:00:00'), 'MMM d, yyyy')}</p>
                            {record.shift_start_time && (
                              <p className="text-xs text-slate-500">
                                {record.shift_start_time} - {record.shift_end_time}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">No shift</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {clockIn ? (
                          <div>
                            <p className="text-sm text-slate-900">{format(clockIn, 'MMM d, yyyy')}</p>
                            <p className="text-xs text-slate-500">{format(clockIn, 'h:mm a')}</p>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {clockOut ? (
                          <div>
                            <p className="text-sm text-slate-900">{format(clockOut, 'MMM d, yyyy')}</p>
                            <p className="text-xs text-slate-500">{format(clockOut, 'h:mm a')}</p>
                          </div>
                        ) : (
                          <Badge variant="warning">Still Clocked In</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {duration !== null ? (
                          <span className="text-sm text-slate-900">{duration.toFixed(2)} hours</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs">
                          {record.clock_in_ip && (
                            <p className="text-slate-600">In: {record.clock_in_ip}</p>
                          )}
                          {record.clock_out_ip && (
                            <p className="text-slate-600">Out: {record.clock_out_ip}</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

